/**
 * `POST /job-apply/api/cv/upload` — subir la hoja de vida, igual en el alta y en
 * la pantalla «Hoja de vida».
 *
 * ── POR QUÉ SE LEE EL ARCHIVO AL ELEGIRLO ─────────────────────────────────────
 *
 * En Chrome de Android, un archivo elegido desde Drive o desde los documentos de
 * WhatsApp llega como una referencia, no como bytes: la pantalla muestra nombre y
 * tamaño, pero al enviarlo el navegador puede no poder abrirlo y `fetch` falla
 * con «Failed to fetch» sin que la petición salga. Reintentar con el mismo
 * archivo falla igual. Pasó en el alta el 2026-10-12 (+504, «…_copia.pdf», tres
 * intentos, la cuenta sí se creó). Leerlo a memoria apenas se elige convierte
 * ese fallo en un aviso en ese momento, con qué hacer, y deja la subida con
 * bytes que ya no dependen del teléfono.
 *
 * ── POR QUÉ UN REINTENTO ──────────────────────────────────────────────────────
 *
 * Con los bytes en memoria, lo que queda es la red: un corte en datos móviles a
 * mitad de un envío de cientos de KB. Un segundo intento es barato y el servidor
 * no guardó nada si la petición no llegó. Si también falla, se reporta a Sentry
 * con el tipo y el tamaño del archivo para poder distinguir las dos causas.
 */
import * as Sentry from '@sentry/react';
import { apiFetch, NetworkError } from '~/lib/api';
import { messages } from '~/i18n';

export const CV_MAX_SIZE = 5 * 1024 * 1024;

const PDF = 'application/pdf';
const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Lo que acepta el servidor (multer en job-apply-service), y nada más. */
export const CV_ACCEPTED_TYPES = [PDF, DOCX];

/** Pausa antes del segundo intento: deja que la red móvil se recupere. */
const RETRY_DELAY_MS = 1500;

export type CvUploadErrorKind = 'invalid_type' | 'too_large' | 'unreadable' | 'network' | 'http';

export class CvUploadError extends Error {
  readonly kind: CvUploadErrorKind;
  readonly status: number;

  constructor(kind: CvUploadErrorKind, message: string, status = 0) {
    super(message);
    this.name = 'CvUploadError';
    this.kind = kind;
    this.status = status;
  }
}

function texts() {
  return messages().onboarding.upload;
}

/**
 * El tipo del archivo. Algunos teléfonos lo entregan vacío (`''`) para un PDF
 * perfectamente bueno; ahí manda la extensión. Sin tipo el servidor lo descarta.
 */
export function cvMimeType(file: Pick<File, 'type' | 'name'>): string | null {
  if (CV_ACCEPTED_TYPES.includes(file.type)) return file.type;
  if (file.type && file.type !== 'application/octet-stream') return null;
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return PDF;
  if (name.endsWith('.docx')) return DOCX;
  return null;
}

/**
 * Valida el archivo elegido y lo trae a memoria. Devuelve un `File` con los
 * bytes ya leídos y el tipo correcto; lanza `CvUploadError` con el texto para la
 * persona si no sirve o si el teléfono no deja abrirlo.
 */
export async function prepareCvFile(file: File): Promise<File> {
  const type = cvMimeType(file);
  if (!type) throw new CvUploadError('invalid_type', texts().invalidType);
  if (file.size > CV_MAX_SIZE) throw new CvUploadError('too_large', texts().tooLarge);

  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch (err) {
    report('unreadable', file, err);
    throw new CvUploadError('unreadable', texts().unreadable);
  }
  // Un archivo que dice pesar algo y llega vacío es el mismo caso: la
  // referencia ya no apunta a nada.
  if (bytes.byteLength === 0) {
    report('unreadable', file, new Error('empty file'));
    throw new CvUploadError('unreadable', texts().unreadable);
  }
  return new File([bytes], file.name, { type });
}

/**
 * Sube un archivo ya preparado con `prepareCvFile`. Un corte de red se reintenta
 * una vez; una respuesta del servidor (400, 401, 500) no, porque el servidor ya
 * dijo qué pasó y su mensaje es el que se muestra.
 */
export async function uploadCvFile<T>(file: File): Promise<T> {
  let res: Response | null = null;
  for (let attempt = 1; attempt <= 2 && !res; attempt++) {
    try {
      res = await apiFetch('/job-apply/api/cv/upload', { method: 'POST', body: cvFormData(file) });
    } catch (err) {
      if (!(err instanceof NetworkError)) throw err;
      if (attempt === 1) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        continue;
      }
      report('network', file, err.original);
      throw new CvUploadError('network', texts().network);
    }
  }
  // El bucle sale con respuesta o lanzando; esto sólo calma al compilador.
  if (!res) throw new CvUploadError('network', texts().network);

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new CvUploadError('http', body?.message || texts().httpError(res.status), res.status);
  }
  return (await res.json()) as T;
}

/** Un FormData nuevo por intento: no se reutiliza un cuerpo ya enviado. */
function cvFormData(file: File): FormData {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
}

function report(kind: 'unreadable' | 'network', file: File, err: unknown): void {
  Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
    tags: { area: 'cv_upload', cv_upload_failure: kind },
    extra: {
      file_type: file.type || '(vacío)',
      file_size: file.size,
      file_ext: file.name.split('.').pop()?.toLowerCase() ?? '',
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    },
  });
}
